import { type FC, useMemo, useState } from "react";

import moment from "moment";
import { Link } from "react-router-dom";

import {
  Badge,
  CalendarBlank,
  classNames,
  type DateRangePartial,
  DateRangePicker,
  Icon,
  Input,
  Text,
  Tooltip,
} from "@alpacahq/ui/old";
import {
  Table,
  type TableColumn,
  useDataGrid,
} from "@alpacahq/ui/old/datagrid";

import { Path } from "~/v2/path";
import type { SecfiLoan } from "src/v2/api/rest/loans";
import { getPrettyCash, getPrettyDate } from "src/v2/utils/formatting";

interface StockLendingInterestTableProps {
  loans: SecfiLoan[];
  loading?: boolean;
  onSearchChange?: (symbol: string) => void;
  onDateRangeChange?: (dateRange: DateRangePartial) => void;
}

export const StockLendingInterestTable: FC<StockLendingInterestTableProps> = ({
  loans,
  loading = false,
  onSearchChange,
  onDateRangeChange,
}) => {
  const [dateRange, setDateRange] = useState<DateRangePartial>([null, null]);

  const columns = useMemo<TableColumn<SecfiLoan>[]>(
    () => [
      {
        name: "Asset Lent",
        key: "symbol",
        headerClassName: "text-gray-500",
        cellRenderer: ({ symbol }) => (
          <Link
            to={Path.format(Path.ROUTE_TRADE, { symbol })}
            className="font-medium text-indigo-600 text-sm underline hover:text-indigo-700"
          >
            {symbol}
          </Link>
        ),
      },
      {
        name: (
          <Tooltip content="Market value is the closing price of the asset on the date the loan was initiated.">
            <span className="underline decoration-[14%] decoration-dotted underline-offset-2">
              Market Value
            </span>
          </Tooltip>
        ),
        key: "market_value",
        alignRight: true,
        headerClassName: "text-gray-500",
        cellRenderer: ({ market_value }) => (
          <Text className="text-gray-700 text-sm">
            {getPrettyCash(market_value)}
          </Text>
        ),
      },
      {
        name: "Qty",
        key: "quantity",
        alignRight: true,
        headerClassName: "text-gray-500",
        cellRenderer: ({ quantity }) => (
          <Text className="text-gray-700 text-sm">
            {quantity?.toLocaleString() ?? "–"}
          </Text>
        ),
      },
      {
        name: "Collateral Used",
        key: "collateral",
        alignRight: true,
        headerClassName: "text-gray-500",
        cellRenderer: ({ collateral }) => (
          <Text className="text-gray-700 text-sm">
            {getPrettyCash(collateral)}
          </Text>
        ),
      },
      {
        name: (
          <Tooltip content="Interest will be shown once the loan is closed.">
            <span className="underline decoration-[14%] decoration-dotted underline-offset-2">
              Interest
            </span>
          </Tooltip>
        ),
        key: "interest",
        alignRight: true,
        headerClassName: "text-gray-500",
        cellRenderer: ({ interest }) => (
          <Text
            className={classNames(
              "text-sm",
              interest ? "text-emerald-600" : "text-gray-700",
            )}
          >
            {interest
              ? getPrettyCash(interest.customer, {
                  withPrefix: true,
                  forcePrecision: 2,
                  minimumFractionDigits: 2,
                })
              : "-"}
          </Text>
        ),
      },
      {
        name: "Status",
        key: "status",
        alignRight: false,
        headerClassName: "text-gray-500",
        cellRenderer: ({ interest }) => (
          <Badge
            variant={interest ? "v2/gray" : "v2/yellow"}
            className="flex items-center gap-1"
          >
            <span className="font-medium text-xs">
              {interest ? "Closed" : "In-Progress"}
            </span>
          </Badge>
        ),
      },
      {
        name: (
          <Tooltip content="Date loaned is the calendar date when the asset was lent.">
            <span className="underline decoration-[14%] decoration-dotted underline-offset-2">
              Date Loaned
            </span>
          </Tooltip>
        ),
        key: "date",
        headerClassName: "text-gray-500",
        cellRenderer: ({ date }) => (
          <Text className="text-gray-700 text-sm">
            {getPrettyDate(moment(date).toDate().getTime(), "MM/DD/YYYY")}
          </Text>
        ),
      },
    ],
    [],
  );

  const tableProps = useDataGrid({
    rows: loans,
    columns,
    pagination: false,
    search: false,
    columnVisibility: {
      defaultVisibleColumns: [
        "symbol",
        "market_value",
        "quantity",
        "collateral",
        "interest",
        "status",
        "date",
      ],
    },
    sort: true,
    isLoading: loading,
    variant: "modern",
  });

  const customTools = [
    // Search input
    <Input
      key="search"
      placeholder="Search by name..."
      onChange={(e) => onSearchChange?.(e.target.value)}
      className="h-10 w-64 py-5"
      leftIcon={<Icon name="MagnifyingGlass" />}
      variant="modern"
    />,
    // Date range picker
    <DateRangePicker
      key="dateRange"
      placeholder="Transaction Date Range"
      value={dateRange}
      onChange={(dates) => {
        setDateRange(dates);
        onDateRangeChange?.(dates);
      }}
      isClearable
      onClear={() => {
        setDateRange([null, null]);
        onDateRangeChange?.([null, null]);
      }}
      className="h-10 text-black"
      containerClassName="[&>div]:border-gray-300 font-medium"
      icon={<CalendarBlank />}
      variant="modern"
    />,
    // Include the default tools (column visibility controls)
    ...(Array.isArray(tableProps.tools)
      ? tableProps.tools
      : [tableProps.tools].filter(Boolean)),
  ];

  return (
    <Table {...tableProps} emptyMessage="No loans found" tools={customTools} />
  );
};
